import React, { useState } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { ChevronDown, Eye, RotateCcw } from "lucide-react";
import { FormattedMessage } from "react-intl";
import Messages from "../../Messages";
import Text from "../../../components/Text";
import "./VersionHistoryDropdown.scss";

const VersionHistoryDropdown = ({
  versions = [],
  currentVersion,
  currentVersionDate,
  currentVersionUser,
  onRestoreVersion,
  isRestoring,
  isRTL,
  previewThemeStyle = {},
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmVersion, setConfirmVersion] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);

  const toggle = () => setDropdownOpen(!dropdownOpen);

  const handleRestoreClick = (versionNumber) => {
    setConfirmVersion(versionNumber);
    setDropdownOpen(false);
  };

  const handleConfirmRestore = async () => {
    if (confirmVersion && onRestoreVersion) {
      await onRestoreVersion(confirmVersion);
      setConfirmVersion(null);
    }
  };

  const handleCancelRestore = () => {
    setConfirmVersion(null);
  };

  const handlePreviewClick = (version) => {
    setPreviewVersion(version);
    setDropdownOpen(false);
  };

  const handleClosePreview = () => {
    setPreviewVersion(null);
  };

  const handleRestoreFromPreview = () => {
    if (previewVersion) {
      setConfirmVersion(previewVersion.versionNumber);
      setPreviewVersion(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const sortedVersions = [...versions].sort(
    (a, b) => b.versionNumber - a.versionNumber
  );

  return (
    <>
      <Dropdown
        isOpen={dropdownOpen}
        toggle={toggle}
        className="version-history-dropdown"
      >
        <DropdownToggle
          tag="button"
          className="version-badge-btn"
          aria-label="Version History"
        >
          <FormattedMessage
            {...Messages.version_number}
            values={{ number: currentVersion }}
          />
          <ChevronDown size={12} />
        </DropdownToggle>
        <DropdownMenu
          className={`version-dropdown-menu ${isRTL ? "dropdown-menu-rtl" : ""}`}
          end
        >
          <div className="version-dropdown-header">
            <Text content={Messages.version_history} />
          </div>
          <div className="version-dropdown-list">
            {/* Current version */}
            <div className="version-dropdown-item current-version">
              <div className="version-dropdown-item-info">
                <div className="version-dropdown-item-number">
                  <FormattedMessage
                    {...Messages.version_number}
                    values={{ number: currentVersion }}
                  />
                  <span className="version-current-badge">
                    <Text content={Messages.current_version} />
                  </span>
                </div>
                <div className="version-dropdown-item-meta">
                  {currentVersionDate && (
                    <span className="version-dropdown-item-date">
                      {formatDate(currentVersionDate)}
                    </span>
                  )}
                  {currentVersionUser && (
                    <span className="version-dropdown-item-user">
                      {currentVersionUser}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Previous versions */}
            {sortedVersions.length === 0 ? (
              <div className="version-dropdown-empty">
                <Text content={Messages.no_versions} />
              </div>
            ) : (
              sortedVersions.map((version) => (
                <div key={version.versionNumber} className="version-dropdown-item">
                  <div className="version-dropdown-item-info">
                    <div className="version-dropdown-item-number">
                      <FormattedMessage
                        {...Messages.version_number}
                        values={{ number: version.versionNumber }}
                      />
                    </div>
                    <div className="version-dropdown-item-meta">
                      <span className="version-dropdown-item-date">
                        {formatDate(version.modifiedDate)}
                      </span>
                      {version.modifiedByUserName && (
                        <span className="version-dropdown-item-user">
                          {version.modifiedByUserName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="version-dropdown-item-actions">
                    <button
                      className="version-action-btn"
                      onClick={() => handlePreviewClick(version)}
                      title="Preview"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="version-action-btn"
                      onClick={() => handleRestoreClick(version.versionNumber)}
                      disabled={isRestoring}
                      title="Restore"
                    >
                      <RotateCcw size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DropdownMenu>
      </Dropdown>

      {/* Restore Confirmation Modal */}
      <Modal
        isOpen={confirmVersion !== null}
        toggle={handleCancelRestore}
        className="version-restore-confirm-modal"
        size="sm"
        zIndex={1090}
        style={previewThemeStyle}
      >
        <ModalHeader toggle={handleCancelRestore}>
          <Text content={Messages.restore_version} />
        </ModalHeader>
        <ModalBody>
          <Text content={Messages.restore_version_confirm} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancelRestore}>
            <FormattedMessage {...Messages.cancel} />
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmRestore}
            disabled={isRestoring}
          >
            {isRestoring ? (
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              <Text content={Messages.restore_version} />
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={previewVersion !== null}
        toggle={handleClosePreview}
        className={`version-preview-modal ${isRTL ? "rtl" : ""}`}
        style={{ ...previewThemeStyle, direction: isRTL ? "rtl" : "ltr" }}
        size="lg"
        zIndex={1090}
      >
        <ModalHeader toggle={handleClosePreview}>
          <FormattedMessage
            {...Messages.version_number}
            values={{ number: previewVersion?.versionNumber }}
          />
          {previewVersion && (
            <span className="version-preview-meta">
              {formatDate(previewVersion.modifiedDate)}
              {previewVersion.modifiedByUserName &&
                ` - ${previewVersion.modifiedByUserName}`}
            </span>
          )}
        </ModalHeader>
        <ModalBody className="version-preview-body">
          {/* Render with the same .ProseMirror design-system styling + theme
              vars the edit modal uses, so the preview matches the document. */}
          <div
            className="version-preview-content ProseMirror"
            style={previewThemeStyle}
            dangerouslySetInnerHTML={{ __html: previewVersion?.content || "" }}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClosePreview}>
            <FormattedMessage {...Messages.close} />
          </Button>
          <Button
            color="primary"
            onClick={handleRestoreFromPreview}
            disabled={isRestoring}
          >
            <RotateCcw size={14} className="me-2" />
            <Text content={Messages.restore_version} />
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VersionHistoryDropdown;
